import { useEffect } from 'react';

import { useCheckoutContext, Article } from '@zooplus/checkout-state';

import CartEmpty from '@components/cart/cart-empty';
import CartOverview from '@components/cart/cart-overview';
import { EmptyCartSkeleton } from '@components/common/checkout-skeleton';
import GeneralErrorMessage from '@components/common/notifications/general-error-message';
import PageLoader from '@components/common/page-loader';
import useCartTracking from '@hooks/use-adobe-tracking/use-cart-tracking';
import usePageTracking from '@hooks/use-adobe-tracking/use-page-tracking';
import {
  CHECKOUT_STATE_PROJECT_ID,
  PAGE_TYPE,
  ADOBE_TRACKING,
  TRACKING_DATA_PAGE,
} from '@utilities/constants';
import { goToCart, isArrayNotEmpty, isObjectNotNullOrEmpty } from '@utilities/utils';

const Cart = () => {
  const {
    checkoutState,
    isFetching,
    mainTogglesMap = {},
    pageType,
  } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const { trackCartView, trackCartViewUsingTrackerListener } = useCartTracking();
  const { trackPageLoadEvent, trackPageViewUsingAdobe, trackPageViewUsingTrackerListener } =
    usePageTracking();

  const articles = checkoutState?.cart?.articles;
  const togglesAndExperiments = checkoutState?.togglesAndExperiments;
  const hasTogglesAndExperiments: boolean = (togglesAndExperiments?.length || 0) > 0;
  const isNewOverviewPage = mainTogglesMap.CHECKOUT_FRONTEND_NEW_OVERVIEW_PAGE;
  const sid = checkoutState?.cart.sid;

  useEffect(() => {
    goToCart(!!isFetching, isNewOverviewPage, isArrayNotEmpty(articles as Article[]));
  }, [articles, isFetching, isNewOverviewPage]);

  useEffect(() => {
    trackPageLoadEvent(ADOBE_TRACKING.PAGE_LOAD_INIT);

    const handleLoad = () => trackPageLoadEvent(ADOBE_TRACKING.PAGE_LOAD_COMPLETE);

    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
    }

    return () => window.removeEventListener('load', handleLoad);
  }, [trackPageLoadEvent]);

  useEffect(() => {
    hasTogglesAndExperiments && trackPageViewUsingAdobe(window.location.href);
  }, [hasTogglesAndExperiments, trackPageViewUsingAdobe]);

  useEffect(() => {
    if (sid) {
      trackPageViewUsingTrackerListener(TRACKING_DATA_PAGE.CHECKOUT_CART);
    }
  }, [sid]);

  useEffect(() => {
    if (checkoutState?.cart && !isFetching) {
      (async () => {
        await trackCartViewUsingTrackerListener();
      })();
    }
  }, [checkoutState?.cart, isFetching]);

  useEffect(() => {
    if (articles !== undefined) {
      trackCartView();
    }
  }, [articles, trackCartView]);

  useEffect(() => {
    const isWebEmptyCart = pageType === PAGE_TYPE.WEB_EMPTY_CART;
    const hasOneArticle = articles?.length === 1;

    if (isWebEmptyCart && hasOneArticle) {
      window.scrollTo(0, 0);
    }
  }, [pageType, articles?.length]);

  const isCartObjectEmpty = !isObjectNotNullOrEmpty(checkoutState?.cart);
  const shouldShowOverview = isNewOverviewPage && isArrayNotEmpty(articles as Article[]);

  if (isFetching) {
    return (
      <>
        <PageLoader />
        <EmptyCartSkeleton />
      </>
    );
  }

  if (isCartObjectEmpty) {
    return <GeneralErrorMessage />;
  }

  if (shouldShowOverview) {
    return <CartOverview />;
  }

  return <CartEmpty />;
};

export default Cart;
